import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {isProOrPartyPassUser} from '~/CreateFabricInvitation/utils/experiments';
import {useEmailPreview} from '~/common/fabric/Card/components/EventPreview/hooks/useEmailPreview';
import {usePreviewInvitation} from '~/common/fabric/Card/components/EventPreview/hooks/usePreviewInvitation';
import {selectGuestCount} from '~/common/fabric/EventGuests/reducers/guests/selectors';
import {UPSELL_MODES, UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {ClickOpenMenu} from '~library/molecules/ClickOpenMenu';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {ProLogoMobileIcon} from '~library/svg/v2/ProLogoMobileIcon';

import {premiumColor, white} from '~libSass/base/_exports.sass';

export const NamedStepsNavCTAs = ({
  isPremium = false,
  currentRoute,
  premiumPreview = false,
  onSave,
  eventId,
  handleNextClick,
  showUpsellModal,
  handleModals,
}) => {
  const isMediumAndUp = useIsMediumAndUp();
  const guestCount = useSelector(selectGuestCount);
  const isReviewStep = currentRoute.name === 'preview';
  const nextIsCheckout = currentRoute.nextRoute === 'review';
  const isFinalFreeStep = !isPremium && currentRoute.name === 'preview' && guestCount === 0;
  const isFinalFreeWithGuestsStep = !isPremium && currentRoute.name === 'guests' && guestCount > 0;
  const isFinalFreemiumStep = isPremium && currentRoute.name === 'preview' && nextIsCheckout;
  const isFinalPremiumStep = isPremium && currentRoute.name === 'guests';
  const isFinalStep = useMemo(
    () => isFinalFreeStep || isFinalPremiumStep || isFinalFreemiumStep || isFinalFreeWithGuestsStep,
    [isFinalFreeStep, isFinalPremiumStep, isFinalFreemiumStep, isFinalFreeWithGuestsStep]
  );
  const isCheckoutStep = currentRoute.name === 'review';

  const isLargishAndUp = useIsLargishAndUp();

  const onPreviewClickHandler = useCallback(
    (callback) => async () => {
      const savePromise = onSave();
      callback(savePromise);
      await savePromise;
    },
    [onSave]
  );
  const {sendEmailPreview} = useEmailPreview({eventId});
  const {onPreviewInvitationClick} = usePreviewInvitation({eventId});
  const showProLogo = isProOrPartyPassUser(window?.subscriptionPlanName);

  return (
    <div className="named-steps-nav__right">
      {((currentRoute.previewButtons && !premiumPreview) ||
        isReviewStep ||
        (isCheckoutStep && isLargishAndUp)) && (
        <ClickOpenMenu
          buttonSize={isMediumAndUp ? 'medium' : 'small'}
          title="Preview"
          trigger={
            <Typography variant="paragraph2" color="charcoal" className="named-steps-nav__preview">
              Preview {isMediumAndUp ? <ChevronIcon ratio={0.75} direction="down" /> : null}
            </Typography>
          }
        >
          <li className="clickOpenMenu__li">
            <Button
              variant="transparent"
              className="clickOpenMenu__item"
              data-qa-id="preview-as-guest-btn"
              onClick={onPreviewClickHandler(onPreviewInvitationClick)}
              disableProcessing
            >
              <Typography variant="paragraph2">Preview as guest</Typography>
            </Button>
          </li>
          <li className="clickOpenMenu__li">
            <Button
              variant="transparent"
              className="clickOpenMenu__item"
              data-qa-id="email-preview-btn"
              onClick={onPreviewClickHandler(sendEmailPreview)}
              disableProcessing
            >
              <Typography variant="paragraph2">Email me a preview</Typography>
            </Button>
          </li>
        </ClickOpenMenu>
      )}

      {!isCheckoutStep && (
        <Button
          variant="primary"
          size={isMediumAndUp ? 'medium' : 'small'}
          data-qa-id="next"
          onClick={handleNextClick}
          disableProcessing
        >
          <Typography variant="paragraph2" size="medium">
            {currentRoute.nextButtonText || 'Next'}
          </Typography>{' '}
          {!isFinalStep ? (
            <ArrowIcon direction="right" ratio={isMediumAndUp ? 0.75 : 0.5} color={white} />
          ) : null}
        </Button>
      )}
      {!isMediumAndUp && isCheckoutStep && (isPremium || showProLogo) ? (
        <Typography
          variant="paragraph2"
          color="premiumColor"
          className="named-steps-nav__premium-badge"
        >
          {showProLogo ? (
            <ProLogoMobileIcon />
          ) : (
            <>
              <DiamondFillIcon color={premiumColor} ratio={0.25} /> Premium
            </>
          )}
        </Typography>
      ) : null}

      {showUpsellModal ? (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={handleModals.toggleUpsellModal}
          closeOverlay={handleModals.handleCloseUpsell}
          onDenyAction={handleModals.downgradeBackToBasic}
          onConfirmAction={handleModals.upgradeAndContinue}
          openTray="customize"
          mode={premiumPreview ? UPSELL_MODES.STEP_1_COPY : UPSELL_MODES.UNLOCK_DESIGN_TOOLS}
          cancelCtaText="Dismiss"
          hideSubtitle={!premiumPreview}
        />
      ) : null}
    </div>
  );
};
